import { useCallback, useEffect, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Paper, Select, Button, FormControlLabel, Checkbox } from '@mui/material';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Typography from '@mui/material/Typography';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useNavigate } from 'react-router';
import { fetchSubmittedClaimsChart, exportSubmittedClaimsChart, MenuProps } from '../../Widgets/WidgetService';
import EncounterSummary from '../../../Models/Widget/EncounterSummary.model';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { parentUrl } from '../../../utils/constants';
import { generateRandomId } from '../../../utils/common';
import b64toBlob from 'b64-to-blob';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import HealthPlanMultiSelect from '../../Shared/HealthPlanMultiSelect';
import { useSearchParams } from 'react-router-dom';
import '../Widgets.css';

const SubmittedClaimSummaryDetail = () => {
    const dispatch = useAppDispatch();
    const history = useNavigate();
    const [searchParams] = useSearchParams();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [members, setMembers] = useState<EncounterSummary>();
    const {
        submittedClaimSelectedProvider,
        setSubmittedClaimSelectedProvider,
        submittedClaimSelectedPlans,
        setSubmittedClaimSelectedPlans,
        submittedClaimSelectedTeam,
        setSubmittedClaimSelectedTeam,
        submittedClaimShowInActiveTeams,
        setSubmittedClaimShowInActiveTeams,
        submittedClaimShowInActiveProviders,
        setSubmittedClaimShowInActiveProviders,
    } = useWidgetContext();
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const filteredProviders = (submittedClaimShowInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const hasElevatedFilterAccess = searchParams.get('hasElevatedFilterAccess') === 'true';

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
    }, [isFetched]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        const changeFilter = async () => {
            setIsMembersLoading(false);
            const data = await fetchSubmittedClaimsChart(
                submittedClaimSelectedProvider,
                submittedClaimSelectedTeam,
                submittedClaimSelectedPlans.join(","),
                submittedClaimShowInActiveTeams,
                submittedClaimShowInActiveProviders,
                true
            );
            setMembers(data);
            setIsMembersLoading(true);
            setIsFetched(true);
        };
        changeFilter();
    }, [
        submittedClaimSelectedProvider,
        submittedClaimSelectedTeam,
        submittedClaimSelectedPlans,
        submittedClaimShowInActiveTeams,
        submittedClaimShowInActiveProviders,
    ]);

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const toggleProviderHandler = (isChecked: boolean) => {
        setSubmittedClaimShowInActiveProviders(isChecked);
        setSubmittedClaimSelectedProvider('All');
    };

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return <Typography>{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>;
            },
        },
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateInfo',
            headerName: 'Date Of Service',
            width: 150,
        },
    ];

    const columnList: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return <Typography>{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>;
            },
        },
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateInfo',
            headerName: 'Date Of Service',
            width: 150,
        },
    ];

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportSubmittedClaimsChart(
            submittedClaimSelectedProvider,
            submittedClaimSelectedTeam,
            submittedClaimSelectedPlans.join(","),
            submittedClaimShowInActiveTeams,
            submittedClaimShowInActiveProviders
        );
        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `SubmittedClaims - ${dateInfo}.xlsx`);
        link.click();
        link.remove();

        setDownLoading(false);
    };

    return (
        <Grid size={12}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Submitted Claims Within 72 Hours
                        </Typography>
                    </Grid>
                    {
                        hasElevatedFilterAccess && (<Grid size={2}>
                            <TeamDropdown
                                teams={teams}
                                selectedTeam={submittedClaimSelectedTeam}
                                onChange={(id) => setSubmittedClaimSelectedTeam(id)}
                                showInactive={submittedClaimShowInActiveTeams}
                                onToggleInactive={setSubmittedClaimShowInActiveTeams}
                            />
                        </Grid>)
                    }
                    <Grid size={2}>
                        <HealthPlanMultiSelect
                            value={submittedClaimSelectedPlans}
                            onChange={setSubmittedClaimSelectedPlans}
                            menuProps={MenuProps}
                        />
                    </Grid>
                    <Grid size={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Providers</InputLabel>
                            <Select
                                value={submittedClaimSelectedProvider}
                                label="Providers"
                                name="Providers"
                                onChange={(e) => setSubmittedClaimSelectedProvider(e.target.value)}
                                MenuProps={MenuProps}
                            >
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                <FormControlLabel
                                    sx={{ pl: '12px', mb: '0px' }}
                                    control={
                                        <Checkbox checked={submittedClaimShowInActiveProviders} onChange={(e) => toggleProviderHandler(e.target.checked)} />
                                    }
                                    label="Show Inactive"
                                />
                                {filteredProviders.map((option) => {
                                    return (
                                        <MenuItem key={option.Id} value={option.Id} sx={{ color: option.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                            {`${option.LastName}, ${option.FirstName}`}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={hasElevatedFilterAccess ? 5 : 7}></Grid>
                    <Grid size={1}>
                        <Button style={{ minWidth: '120px', marginLeft: 'auto' }} onClick={() => exportDetail()} variant="contained" color="success">
                            {downLoading ? 'Exporting..' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} minHeight="60vh" sx={{ position: 'relative' }}>
                        {!isMembersLoading ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container columnSpacing={3}>
                                {members.Encounters.map((detail, index) => (
                                    <>
                                        <Grid size={12} textAlign="center" paddingTop={index === 0 ? 0 : 2}>
                                            <Typography display={'inline-block'} variant="h5" color={'default'} marginTop={1}>
                                                {detail.Date}
                                            </Typography>
                                            <hr></hr>
                                        </Grid>
                                        <Grid size={6} textAlign="center">
                                            <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                                Total Encounters ({detail.EncounterCount})
                                            </Typography>
                                            <Grid size={12} style={{ height: '67vh', width: '100%' }}>
                                                <DataGrid
                                                    columns={columns}
                                                    rows={detail.TotalEncounters}
                                                    getRowId={(row) => `${row.Id}-${row.DateInfo}-${generateRandomId()}`}
                                                    paginationMode="server"
                                                    hideFooterPagination={true}
                                                    hideFooter={true}
                                                    onCellClick={(params: any) => {
                                                        window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid size={6} textAlign="center">
                                            <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                                Submitted Encounters({detail.CleanClaimCount})
                                            </Typography>
                                            <Grid size={12} style={{ height: '67vh', width: '100%' }}>
                                                <DataGrid
                                                    columns={columnList}
                                                    rows={detail.SubmittedEncounters}
                                                    getRowId={(row) => `${row.ItemId}-${generateRandomId()}`}
                                                    paginationMode="server"
                                                    hideFooterPagination={true}
                                                    hideFooter={true}
                                                    onCellClick={(params: any) => {
                                                        window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                ))}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default SubmittedClaimSummaryDetail;
